import { createContext, useContext, useState, useEffect } from 'react'
import { subscribeToDocument } from '../services/firebase'

// Default site settings
const defaultSettings = {
  churchName: 'DLBC Clarksville TN',
  tagline: 'A Disciple Making Church',
  fullName: 'Deeper Life Bible Church Clarksville TN',
  address: {
    street: '819 Providence Blvd, Suite B',
    city: 'Clarksville',
    state: 'TN',
    zip: '37042',
    country: 'USA',
  },
  phone: '(931) 266-1138',
  email: 'info@dlbcclarksville.org',
  serviceTimes: [
    { day: 'Sunday', name: 'Devotional Worship', time: '9:00 AM' },
    { day: 'Monday', name: 'Bible Study', time: '6:00 PM' },
    { day: 'Friday', name: 'Revival Hour', time: '6:00 PM' },
    { day: 'Various', name: 'Small Group Meetings', time: '5:00 PM' },
  ],
  socialMedia: {
    facebook: 'https://www.facebook.com/dlbcclarksvilletn',
    youtube: 'https://www.youtube.com/playlist?list=PLhUSSHKj-nG_f2IOOj6Au917Juj0lVVcl',
    instagram: 'https://instagram.com/dlbcclarksville',
  },
  liveStreamUrl: '',
  officeHours: 'Monday - Friday: 9:00 AM - 5:00 PM',
  footerText: '© 2024 Deeper Life Bible Church Clarksville. All rights reserved.',
  metaDescription: 'Deeper Life Bible Church Clarksville - A place of worship, growth, and community.',
  metaKeywords: 'church, Clarksville, Tennessee, worship, Bible, community',
}

const SiteContext = createContext(null)

export function SiteProvider({ children }) {
  const [settings, setSettings] = useState(defaultSettings)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Subscribe to site settings from Firestore
    const unsubscribe = subscribeToDocument('settings', 'site', (data) => {
      if (data) {
        setSettings({ ...defaultSettings, ...data })
      }
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const getFullAddress = () => {
    const { street, city, state, zip } = settings.address
    return `${street}, ${city}, ${state} ${zip}`
  }

  const getGoogleMapsUrl = () => {
    const address = encodeURIComponent(getFullAddress())
    return `https://www.google.com/maps/search/?api=1&query=${address}`
  }

  const getNextService = () => {
    const now = new Date()
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const currentDay = dayNames[now.getDay()]
    const currentTime = now.getHours() * 60 + now.getMinutes()

    // Find the next upcoming service
    for (let i = 0; i < 7; i++) {
      const checkDay = dayNames[(now.getDay() + i) % 7]
      const dayServices = settings.serviceTimes.filter(s => s.day === checkDay)

      for (const service of dayServices) {
        const [time, period] = service.time.split(' ')
        const [hours, minutes] = time.split(':').map(Number)
        let serviceMinutes = hours * 60 + minutes
        if (period === 'PM' && hours !== 12) serviceMinutes += 12 * 60
        if (period === 'AM' && hours === 12) serviceMinutes -= 12 * 60

        if (i === 0 && serviceMinutes > currentTime) {
          return { ...service, daysUntil: 0 }
        } else if (i > 0) {
          return { ...service, daysUntil: i }
        }
      }
    }

    return settings.serviceTimes[0] ? { ...settings.serviceTimes[0], daysUntil: 7 } : null
  }

  const value = {
    settings,
    loading,
    getFullAddress,
    getGoogleMapsUrl,
    getNextService,
  }

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  )
}

export function useSite() {
  const context = useContext(SiteContext)
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider')
  }
  return context
}

export default SiteContext
